import React, { useState } from 'react';

import { useMutation, useInfiniteQuery } from 'react-query';
import UseModal from 'hooks/use-modal';
import UseToast from 'hooks/use-toast';

import { courseSendFeedbackService, lectureSendFeedbackService } from 'services/core';
import { CourseComments, LectureComments } from 'services/core';

import {
	CONTENT_COMMENTS_PAGE_SIZE,
	contentCommentsQueryKey
} from 'constants/content-comments.const';

import Modal from '@core/components/Modal/Modal';
import ActionButton from '@core/components/actionbutton/ActionButton';

import EmptyStar from 'assets/icons/star-rate-empty.svg';
import FilledStar from 'assets/icons/star-rating.svg';

import classes from './FeedbackButton.module.scss';
import { MOBILE_FULLSCREEN } from '@core/constants/ModalTypes';

const FeedbackButton = ({ contentType, contentID, refetchFeedbackSummary }) => {
	const { show, showModal, destroyModal } = UseModal();
	const { ToastContainer, toast } = UseToast();
	const [reviewCompleted, setReviewCompleted] = useState(false);
	const [reviewText, setReviewText] = useState();
	const [rateLimint] = useState([1, 2, 3, 4, 5]);
	const [rate, setRate] = useState(0);
	const {
		mutate: sendFeedBack,
		data,
		isLoading
	} = useMutation(
		contentType === 'course' ? courseSendFeedbackService : lectureSendFeedbackService,
		{
			onSuccess: () => {
				setReviewCompleted(true);
				refetchContentComments();
			},
			onError: (error) => {
				let toasterror = 'Something went wrong!';
				typeof error.response.data.data === 'string' && (toasterror = error.response.data.data);
				typeof error.response.data.data === 'object' &&
					(toasterror =
						error.response.data.data?.messages && error.response.data.data?.messages[0]?.message);
				toast(toasterror || 'Something went wrong!', 'Alert');
			}
		}
	);
	const { refetch: refetchContentComments } = useInfiniteQuery(
		contentCommentsQueryKey(contentType, contentID),
		({ pageParam = 1 }) =>
			contentType === 'course'
				? CourseComments({ id: contentID, page_size: CONTENT_COMMENTS_PAGE_SIZE, page: pageParam })
				: LectureComments({
						id: contentID,
						page_size: CONTENT_COMMENTS_PAGE_SIZE,
						page: pageParam
				  }),
		{ enabled: false }
	);
	const getRatingText = () => {
		switch (rate) {
			case 1:
				return 'Very bad';
			case 2:
				return 'Bad';
			case 3:
				return 'Not so special';
			case 4:
				return 'Good';
			case 5:
				return 'Very Good';
			default:
				return ' ';
		}
	};
	const closeModalHandler = () => {
		setReviewText('');
		setRate(0);
		destroyModal();
		reviewCompleted && refetchFeedbackSummary();
	};
	const feedbackHandler = () => {
		sendFeedBack({
			id: contentID,
			rate,
			comment: reviewText
		});
	};
	return (
		<>
			<ActionButton
				onClick={showModal}
				kind="secondary"
				className={`${classes['feedback-action']}`}>
				Share your review
			</ActionButton>
			<Modal
				title="Share review"
				type={MOBILE_FULLSCREEN}
				closeHandler={closeModalHandler}
				show={show}>
				<div className={`${classes['review-modal']} d-flex flex-column justify-content-center`}>
					{reviewCompleted ? (
						<div className={`${classes['review-modal__completed']}`}>
							<h1>Thank you for your feedback</h1>
							<p>
								your review both help us to make our contents better and other people to choose the
								right content.
							</p>
							<p>This is how other people will see your review</p>
							<div className={`${classes['commend-card']}`} key={data?.data?.id}>
								<div className="d-flex flex-row align-items-center">
									<h4>{data?.data?.name}</h4>
									<span />
									<FilledStar data-testid="filled-star" />
									<h5>{data?.data?.rate}</h5>
								</div>
								<h6>Reviewed on {data?.data?.date}</h6>
								<p>{data?.data?.comment}</p>
							</div>
						</div>
					) : (
						<div className={`${classes['review-modal__init']} d-flex flex-column`}>
							<h4>How would you rate your feedback?</h4>
							<div>
								{rateLimint.map((starRate) => (
									<button key={starRate} onClick={() => setRate(starRate)}>
										{starRate <= rate ? (
											<FilledStar data-testid="filled-star" />
										) : (
											<EmptyStar data-testid="empty-star" />
										)}
									</button>
								))}
								<p className="my-0">{getRatingText()}</p>
							</div>
							<label htmlFor="review">Write your review</label>
							<textarea
								name="review"
								id="review"
								onChange={(e) => setReviewText(e.target.value)}
								placeholder="Tell us more about your feedback"
							/>
							<ActionButton
								disabled={!rate}
								loading={isLoading}
								onClick={feedbackHandler}>
								Submit review
							</ActionButton>
						</div>
					)}
				</div>
			</Modal>
			<ToastContainer />
		</>
	);
};

export default FeedbackButton;
